import React, { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { Button, ButtonBaseActions, makeStyles, TextField, Typography } from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import RedoIcon from '@material-ui/icons/Redo';
import UndoIcon from '@material-ui/icons/Undo';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { PopupConfirmation } from 'components/shared';
import { openingsLostByShapeChoice, useTableMapWizard } from '../context';
import CanvasStepLayout, { CanvasStepHeading } from '../components/CanvasStepLayout';
import MapEditorCanvas, { clientToSVG } from '../components/MapEditorCanvas';
import RoomDrawLayer from '../components/RoomDrawLayer';
import RoomDrawPanel from '../components/RoomDrawPanel';
import ZoomControls from '../components/ZoomControls';
import { drawSessionReducer, INITIAL_DRAW_SESSION } from '../drawSession';
import {
    AreaPreset,
    areaBBoxPx,
    clamp,
    computeRoom,
    detectPreset,
    EditorArea,
    POLYGON_PRESETS,
    Point,
    SizeMeters,
    snapMeters,
    snapPointMeters,
    TABLE_SHAPES,
    TABLE_SIZE_MAX,
    TABLE_SIZE_MIN,
    TableShape,
} from '../mapGeometry';
import { openingName } from '../openings';
import { areaToCorners, cornersToArea } from '../roomOutline';
import useNumericField from '../useNumericField';

const MAGENTA = theme.palette.primary.main;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';

const MAP_SIZE_MIN = 1;
const MAP_SIZE_MAX = 100;
const AREA_BBOX_MIN = 0.5;
const CORNER_HANDLE_RADIUS = 6;

const TABLE_SHAPE_LABEL_KEY: Record<TableShape, string> = {
    round: 'tableMaps.wizard.roundTable',
    square: 'tableMaps.wizard.squareTable',
    rectangle: 'tableMaps.wizard.rectangleTable',
};

const AREA_PRESETS: AreaPreset[] = ['rectangle', 'square', 'circle', 'oval', 'L', 'T', 'U'];
const PRESET_LABEL_KEY: Record<AreaPreset, string> = {
    rectangle: 'tableMaps.wizard.areaShapeRectangle',
    square: 'tableMaps.wizard.areaShapeSquare',
    circle: 'tableMaps.wizard.areaShapeCircle',
    oval: 'tableMaps.wizard.areaShapeOval',
    L: 'tableMaps.wizard.areaShapeL',
    T: 'tableMaps.wizard.areaShapeT',
    U: 'tableMaps.wizard.areaShapeU',
};

function applyPreset(preset: AreaPreset, area: EditorArea): Partial<EditorArea> {
    switch (preset) {
        case 'rectangle':
            return { kind: 'Rectangle', points: null };
        case 'square': {
            const side = Math.min(area.size.width, area.size.height);
            return { kind: 'Rectangle', points: null, size: { width: side, height: side } };
        }
        case 'circle': {
            const side = Math.min(area.size.width, area.size.height);
            return { kind: 'Ellipse', points: null, size: { width: side, height: side } };
        }
        case 'oval':
            return { kind: 'Ellipse', points: null };
        case 'L':
        case 'T':
        case 'U':
            return { kind: 'Polygon', points: POLYGON_PRESETS[preset] };
    }
}

interface PendingConfirm {
    title: string;
    description: string;
    confirmLabel: string;
    action: () => void;
}

const useStyles = makeStyles({
    drawPanel: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    drawStrip: {
        padding: '10px 16px',
        borderRadius: 4,
        backgroundColor: MAGENTA_LIGHT,
        fontSize: 13,
        color: theme.palette.text.primary,
    },
    historyButton: {
        minWidth: 40,
        height: 32,
        padding: 0,
        color: theme.palette.text.primary,
        borderColor: theme.palette.grey['300'],
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    gridLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
    },
    gridToggleButton: {
        minWidth: 48,
        height: 32,
        padding: '0 12px',
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey['300']}`,
        cursor: 'pointer',
        letterSpacing: 0.5,
    },
    gridToggleOn: {
        backgroundColor: MAGENTA,
        color: 'white',
        borderColor: MAGENTA,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    gridToggleOff: {
        backgroundColor: 'white',
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
    },
    settingsPanel: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    panelHeading: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        marginBottom: 4,
    },
    fieldRow: {
        display: 'flex',
        gap: 12,
    },
    defaultSizeList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
    },
    defaultSizeLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: 4,
    },
    presetGrid: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 8,
    },
    presetButton: {
        minWidth: 0,
        flex: '1 1 30%',
        height: 32,
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey['300']}`,
    },
    presetButtonActive: {
        backgroundColor: MAGENTA,
        color: 'white',
        borderColor: MAGENTA,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    presetButtonInactive: {
        backgroundColor: 'white',
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
    },
    drawManuallyButton: {
        flex: '2 1 60%',
        position: 'relative' as const,
        overflow: 'visible',
    },
    newBadge: {
        position: 'absolute' as const,
        top: -8,
        right: -4,
        padding: '1px 6px',
        borderRadius: 8,
        fontSize: 9,
        fontWeight: 700,
        lineHeight: '14px',
        letterSpacing: 0.4,
        backgroundColor: MAGENTA,
        color: 'white',
        textTransform: 'uppercase' as const,
    },
    emptyState: {
        position: 'absolute' as const,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        textAlign: 'center' as const,
        padding: 24,
    },
    emptyStateTitle: {
        fontSize: 15,
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    emptyStateText: {
        fontSize: 13,
        color: theme.palette.text.secondary,
    },
    emptyStateActions: {
        display: 'flex',
        gap: 12,
    },
    hintText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
});

interface DefaultTableSizeRowProps {
    shape: TableShape;
    size: SizeMeters;
    maxSize: SizeMeters;
    onChange: (size: SizeMeters) => void;
}

function DefaultTableSizeRow({ shape, size, maxSize, onChange }: DefaultTableSizeRowProps) {
    const classes = useStyles();
    const widthField = useNumericField(
        size.width,
        (n) => n >= TABLE_SIZE_MIN && n <= maxSize.width,
        (width) => onChange({ width, height: size.height })
    );
    const heightField = useNumericField(
        size.height,
        (n) => n >= TABLE_SIZE_MIN && n <= maxSize.height,
        (height) => onChange({ width: size.width, height })
    );

    return (
        <div>
            <Typography className={classes.defaultSizeLabel}>
                {i18n.t(TABLE_SHAPE_LABEL_KEY[shape])}
            </Typography>
            <div className={classes.fieldRow}>
                <TextField
                    label={i18n.t('tableMaps.wizard.tableWidth')}
                    type="number"
                    value={widthField.input}
                    onChange={widthField.onChange}
                    variant="filled"
                    fullWidth
                    inputProps={{ min: TABLE_SIZE_MIN, max: maxSize.width, step: 0.01 }}
                />
                <TextField
                    label={i18n.t('tableMaps.wizard.tableLength')}
                    type="number"
                    value={heightField.input}
                    onChange={heightField.onChange}
                    variant="filled"
                    fullWidth
                    inputProps={{ min: TABLE_SIZE_MIN, max: maxSize.height, step: 0.01 }}
                />
            </div>
        </div>
    );
}

export default function MapConfigurationStep() {
    const classes = useStyles();
    const { draft, dispatch, zoom, setZoom } = useTableMapWizard();
    const svgRef = useRef<SVGSVGElement>(null);
    const room = useMemo(() => computeRoom(draft.size), [draft.size.width, draft.size.height]);

    const isDrawing = draft.isDrawingArea;
    const [drawSession, drawDispatch] = useReducer(drawSessionReducer, INITIAL_DRAW_SESSION);
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
    const firstPresetAction = useRef<ButtonBaseActions>(null);

    function handleGridToggle() {
        dispatch({ type: 'SET_GRID_ENABLED', payload: !draft.gridEnabled });
    }

    const mapWidthField = useNumericField(
        draft.size.width,
        (n) => n >= MAP_SIZE_MIN && n <= MAP_SIZE_MAX,
        (width) => dispatch({ type: 'SET_SIZE', payload: { width, height: draft.size.height } })
    );
    const mapHeightField = useNumericField(
        draft.size.height,
        (n) => n >= MAP_SIZE_MIN && n <= MAP_SIZE_MAX,
        (height) => dispatch({ type: 'SET_SIZE', payload: { width: draft.size.width, height } })
    );

    const areaWidthField = useNumericField(
        draft.area.size.width,
        (n) => n >= AREA_BBOX_MIN,
        (width) =>
            dispatch({
                type: 'SET_AREA',
                payload: { size: { width, height: draft.area.size.height } },
            })
    );
    const areaHeightField = useNumericField(
        draft.area.size.height,
        (n) => n >= AREA_BBOX_MIN,
        (height) =>
            dispatch({
                type: 'SET_AREA',
                payload: { size: { width: draft.area.size.width, height } },
            })
    );
    const areaOffsetXField = useNumericField(
        draft.area.offset.x,
        (n) => n >= 0,
        (x) => dispatch({ type: 'SET_AREA', payload: { offset: { x, y: draft.area.offset.y } } })
    );
    const areaOffsetYField = useNumericField(
        draft.area.offset.y,
        (n) => n >= 0,
        (y) => dispatch({ type: 'SET_AREA', payload: { offset: { x: draft.area.offset.x, y } } })
    );

    const activePreset = draft.isShapeChosen ? detectPreset(draft.area) : null;

    function openingsRemovedWarning(shape: Partial<EditorArea>): string {
        const lost = openingsLostByShapeChoice(draft, shape);
        if (lost.length === 0) return '';
        return i18n.t('tableMaps.wizard.features.removedByOutline', {
            names: lost.map((o) => openingName(draft.entrances, o)).join(', '),
        });
    }

    function handlePresetSelect(preset: AreaPreset) {
        const shape = applyPreset(preset, draft.area);
        const choose = () => dispatch({ type: 'CHOOSE_AREA_SHAPE', payload: shape });
        const openingsWarning = openingsRemovedWarning(shape);
        if (activePreset === 'custom') {
            setPendingConfirm({
                title: i18n.t('tableMaps.wizard.draw.replaceCustomTitle'),
                description: [
                    i18n.t('tableMaps.wizard.draw.replaceCustomDescription'),
                    openingsWarning,
                ]
                    .filter(Boolean)
                    .join(' '),
                confirmLabel: i18n.t('tableMaps.wizard.draw.replaceConfirm'),
                action: choose,
            });
            return;
        }
        if (openingsWarning) {
            setPendingConfirm({
                title: i18n.t('tableMaps.wizard.features.removeTitle'),
                description: openingsWarning,
                confirmLabel: i18n.t('tableMaps.wizard.features.removeConfirm'),
                action: choose,
            });
            return;
        }
        choose();
    }

    function startDrawing(corners: Point[]) {
        drawDispatch({ type: 'START', payload: { corners, closed: corners.length > 0 } });
        dispatch({ type: 'SET_DRAWING_AREA', payload: true });
    }

    function handleDrawManually() {
        if (!draft.isShapeChosen) {
            startDrawing([]);
            return;
        }
        const corners = areaToCorners(draft.area);
        if (corners.length > 0) {
            startDrawing(corners);
            return;
        }
        setPendingConfirm({
            title: i18n.t('tableMaps.wizard.draw.discardEllipseTitle'),
            description: i18n.t('tableMaps.wizard.draw.discardEllipseDescription'),
            confirmLabel: i18n.t('tableMaps.wizard.drawManually'),
            action: () => startDrawing([]),
        });
    }

    const handleDiscardDrawing = useCallback(
        () => dispatch({ type: 'SET_DRAWING_AREA', payload: false }),
        [dispatch]
    );

    function handleAcceptDrawing() {
        const shape = cornersToArea(drawSession.outline.corners);
        const accept = () => {
            dispatch({ type: 'CHOOSE_AREA_SHAPE', payload: shape });
            dispatch({ type: 'SET_DRAWING_AREA', payload: false });
        };
        const openingsWarning = openingsRemovedWarning(shape);
        if (!openingsWarning) {
            accept();
            return;
        }
        setPendingConfirm({
            title: i18n.t('tableMaps.wizard.features.removeTitle'),
            description: openingsWarning,
            confirmLabel: i18n.t('tableMaps.wizard.features.removeConfirm'),
            action: accept,
        });
    }

    const maxTableSize: SizeMeters = {
        width: Math.min(TABLE_SIZE_MAX, draft.size.width),
        height: Math.min(TABLE_SIZE_MAX, draft.size.height),
    };

    // Corner resize: drag one corner of the area bbox, opposite corner stays fixed.
    const dragCornerAnchor = useRef<Point | null>(null);

    const handleCornerDown = useCallback(
        (corner: number, e: React.PointerEvent<SVGCircleElement>) => {
            e.stopPropagation();
            e.currentTarget.setPointerCapture(e.pointerId);
            const { offset, size } = draft.area;
            const corners: Point[] = [
                { x: offset.x, y: offset.y },
                { x: offset.x + size.width, y: offset.y },
                { x: offset.x + size.width, y: offset.y + size.height },
                { x: offset.x, y: offset.y + size.height },
            ];
            dragCornerAnchor.current = corners[(corner + 2) % 4];
        },
        [draft.area]
    );

    const handleCornerMove = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            const anchor = dragCornerAnchor.current;
            if (!anchor || !svgRef.current) return;
            const svgPt = clientToSVG(svgRef.current, e.clientX, e.clientY);
            const corner: Point = {
                x: clamp((svgPt.x - room.x) / room.pxPerMeter, 0, draft.size.width),
                y: clamp((svgPt.y - room.y) / room.pxPerMeter, 0, draft.size.height),
            };
            dispatch({
                type: 'SET_AREA',
                payload: {
                    offset: snapPointMeters({
                        x: Math.min(anchor.x, corner.x),
                        y: Math.min(anchor.y, corner.y),
                    }),
                    size: {
                        width: snapMeters(Math.abs(corner.x - anchor.x)),
                        height: snapMeters(Math.abs(corner.y - anchor.y)),
                    },
                },
            });
        },
        [room, draft.size, dispatch]
    );

    const handleCornerUp = useCallback(() => {
        dragCornerAnchor.current = null;
    }, []);

    const bboxPx = areaBBoxPx(draft.area, room);
    const cornersPx: Point[] = [
        { x: bboxPx.x, y: bboxPx.y },
        { x: bboxPx.x + bboxPx.width, y: bboxPx.y },
        { x: bboxPx.x + bboxPx.width, y: bboxPx.y + bboxPx.height },
        { x: bboxPx.x, y: bboxPx.y + bboxPx.height },
    ];

    return (
        <>
            <CanvasStepLayout
                panelSide="left"
                panel={
                    isDrawing ? (
                        <div className={classes.drawPanel}>
                            <Typography className={classes.drawStrip}>
                                {i18n.t('tableMaps.wizard.draw.banner')}
                            </Typography>
                            <RoomDrawPanel
                                title={i18n.t('tableMaps.wizard.draw.title')}
                                session={drawSession}
                                dispatch={drawDispatch}
                                mapSize={draft.size}
                                onDiscard={handleDiscardDrawing}
                                onAccept={handleAcceptDrawing}
                            />
                            <Typography className={classes.hintText}>
                                {i18n.t('tableMaps.wizard.draw.hint')}
                            </Typography>
                        </div>
                    ) : (
                        <div className={classes.settingsPanel}>
                            <div>
                                <Typography className={classes.panelHeading}>
                                    {i18n.t('tableMaps.wizard.mapSize')}
                                </Typography>
                                <div className={classes.fieldRow}>
                                    <TextField
                                        label={i18n.t('tableMaps.wizard.mapWidth')}
                                        type="number"
                                        value={mapWidthField.input}
                                        onChange={mapWidthField.onChange}
                                        variant="filled"
                                        fullWidth
                                        inputProps={{
                                            min: MAP_SIZE_MIN,
                                            max: MAP_SIZE_MAX,
                                            step: 0.01,
                                        }}
                                    />
                                    <TextField
                                        label={i18n.t('tableMaps.wizard.mapLength')}
                                        type="number"
                                        value={mapHeightField.input}
                                        onChange={mapHeightField.onChange}
                                        variant="filled"
                                        fullWidth
                                        inputProps={{
                                            min: MAP_SIZE_MIN,
                                            max: MAP_SIZE_MAX,
                                            step: 0.01,
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <Typography className={classes.panelHeading}>
                                    {i18n.t('tableMaps.wizard.areaShape')}
                                </Typography>
                                <div className={classes.presetGrid}>
                                    {AREA_PRESETS.map((preset, index) => (
                                        <Button
                                            key={preset}
                                            action={index === 0 ? firstPresetAction : undefined}
                                            className={`${classes.presetButton} ${
                                                activePreset === preset
                                                    ? classes.presetButtonActive
                                                    : classes.presetButtonInactive
                                            }`}
                                            onClick={() => handlePresetSelect(preset)}
                                        >
                                            {i18n.t(PRESET_LABEL_KEY[preset])}
                                        </Button>
                                    ))}
                                    <Button
                                        className={`${classes.presetButton} ${
                                            classes.drawManuallyButton
                                        } ${
                                            activePreset === 'custom'
                                                ? classes.presetButtonActive
                                                : classes.presetButtonInactive
                                        }`}
                                        startIcon={<CreateIcon style={{ fontSize: 16 }} />}
                                        onClick={handleDrawManually}
                                    >
                                        {i18n.t('tableMaps.wizard.drawManually')}
                                        <span className={classes.newBadge}>
                                            {i18n.t('tableMaps.wizard.newBadge')}
                                        </span>
                                    </Button>
                                </div>
                            </div>

                            <div>
                                <Typography className={classes.panelHeading}>
                                    {i18n.t('tableMaps.wizard.areaSize')}
                                </Typography>
                                <div className={classes.fieldRow}>
                                    <TextField
                                        label={i18n.t('tableMaps.wizard.areaWidth')}
                                        type="number"
                                        value={areaWidthField.input}
                                        onChange={areaWidthField.onChange}
                                        variant="filled"
                                        fullWidth
                                        inputProps={{ min: AREA_BBOX_MIN, step: 0.01 }}
                                    />
                                    <TextField
                                        label={i18n.t('tableMaps.wizard.areaLength')}
                                        type="number"
                                        value={areaHeightField.input}
                                        onChange={areaHeightField.onChange}
                                        variant="filled"
                                        fullWidth
                                        inputProps={{ min: AREA_BBOX_MIN, step: 0.01 }}
                                    />
                                </div>
                                <div className={classes.fieldRow} style={{ marginTop: 12 }}>
                                    <TextField
                                        label={i18n.t('tableMaps.wizard.areaOffsetX')}
                                        type="number"
                                        value={areaOffsetXField.input}
                                        onChange={areaOffsetXField.onChange}
                                        variant="filled"
                                        fullWidth
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                    <TextField
                                        label={i18n.t('tableMaps.wizard.areaOffsetY')}
                                        type="number"
                                        value={areaOffsetYField.input}
                                        onChange={areaOffsetYField.onChange}
                                        variant="filled"
                                        fullWidth
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                </div>
                            </div>

                            <div>
                                <Typography className={classes.panelHeading}>
                                    {i18n.t('tableMaps.wizard.defaultTableSizes')}
                                </Typography>
                                <div className={classes.defaultSizeList}>
                                    {TABLE_SHAPES.map((shape) => (
                                        <DefaultTableSizeRow
                                            key={shape}
                                            shape={shape}
                                            size={draft.tableDefaults[shape]}
                                            maxSize={maxTableSize}
                                            onChange={(size) =>
                                                dispatch({
                                                    type: 'SET_TABLE_DEFAULT_SIZE',
                                                    payload: { shape, size },
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )
                }
                toolbar={
                    <>
                        <CanvasStepHeading>
                            {i18n.t('tableMaps.wizard.mapConfiguration')}
                        </CanvasStepHeading>
                        <div className={classes.toolbar}>
                            <div className={classes.toolbarLeft}>
                                {isDrawing && (
                                    <>
                                        <Button
                                            variant="outlined"
                                            className={classes.historyButton}
                                            onClick={() => drawDispatch({ type: 'UNDO' })}
                                            disabled={drawSession.past.length === 0}
                                            aria-label={i18n.t('tableMaps.wizard.draw.undo')}
                                            title={i18n.t('tableMaps.wizard.draw.undo')}
                                        >
                                            <UndoIcon fontSize="small" />
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            className={classes.historyButton}
                                            onClick={() => drawDispatch({ type: 'REDO' })}
                                            disabled={drawSession.future.length === 0}
                                            aria-label={i18n.t('tableMaps.wizard.draw.redo')}
                                            title={i18n.t('tableMaps.wizard.draw.redo')}
                                        >
                                            <RedoIcon fontSize="small" />
                                        </Button>
                                    </>
                                )}
                                <Typography className={classes.gridLabel}>
                                    {i18n.t('tableMaps.wizard.grid')}
                                </Typography>
                                <button
                                    className={`${classes.gridToggleButton} ${
                                        draft.gridEnabled
                                            ? classes.gridToggleOn
                                            : classes.gridToggleOff
                                    }`}
                                    onClick={handleGridToggle}
                                    type="button"
                                >
                                    {draft.gridEnabled
                                        ? i18n.t('tableMaps.wizard.on')
                                        : i18n.t('tableMaps.wizard.off')}
                                </button>
                            </div>

                            <ZoomControls
                                zoom={zoom}
                                onZoomChange={setZoom}
                                pxPerMeter={room.pxPerMeter}
                            />
                        </div>
                    </>
                }
            >
                <MapEditorCanvas
                    gridEnabled={draft.gridEnabled}
                    zoom={zoom}
                    size={draft.size}
                    area={draft.area}
                    openings={draft.entrances}
                    features={draft.features}
                    showGlobalRectGuide
                    panOnEmptyDrag={!isDrawing}
                    isDrawMode={isDrawing}
                    showMeasureGuides
                    svgRef={svgRef}
                >
                    {isDrawing ? (
                        <RoomDrawLayer
                            session={drawSession}
                            dispatch={drawDispatch}
                            room={room}
                            mapSize={draft.size}
                            zoom={zoom}
                            svgRef={svgRef}
                        />
                    ) : (
                        cornersPx.map((c, i) => (
                            <circle
                                key={i}
                                cx={c.x}
                                cy={c.y}
                                r={CORNER_HANDLE_RADIUS / zoom}
                                fill="white"
                                stroke={MAGENTA}
                                strokeWidth={2 / zoom}
                                style={{ cursor: 'nwse-resize' }}
                                onPointerDown={(e) => handleCornerDown(i, e)}
                                onPointerMove={handleCornerMove}
                                onPointerUp={handleCornerUp}
                            />
                        ))
                    )}
                </MapEditorCanvas>
                {!draft.isShapeChosen && !isDrawing && (
                    <div className={classes.emptyState}>
                        <Typography className={classes.emptyStateTitle}>
                            {i18n.t('tableMaps.wizard.draw.emptyTitle')}
                        </Typography>
                        <Typography className={classes.emptyStateText}>
                            {i18n.t('tableMaps.wizard.draw.emptyText')}
                        </Typography>
                        <div className={classes.emptyStateActions}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => firstPresetAction.current?.focusVisible()}
                            >
                                {i18n.t('tableMaps.wizard.draw.chooseShape')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDrawManually}
                            >
                                {i18n.t('tableMaps.wizard.drawManually')}
                            </Button>
                        </div>
                    </div>
                )}
            </CanvasStepLayout>

            <PopupConfirmation
                open={pendingConfirm !== null}
                close={() => setPendingConfirm(null)}
                title={pendingConfirm?.title ?? ''}
                description={pendingConfirm?.description ?? ''}
                activeBtn={pendingConfirm?.confirmLabel ?? ''}
                action={() => pendingConfirm?.action()}
            />
        </>
    );
}
